const DEFAULT_ITEMS = ['1', '2', '4', '6', '8', '小王', '大王', '平安夜']
const AUTO_STOP_DELAY_MS = 5000

/**
 * Full-screen penalty wheel overlay.
 * Keeps wheel state mounted and only toggles visibility.
 */
export class PenaltyWheelOverlay {
  private el: HTMLElement
  private items = [...DEFAULT_ITEMS]
  private selectedText = '准备开转'
  private currentRotation = 0
  private spinInterval: number | null = null
  private autoStopTimer: number | null = null
  private isSpinning = false

  constructor() {
    this.el = this.create()
    document.body.appendChild(this.el)
    this.bindEvents()
    this.renderWheel()
    this.syncUi()
  }

  show(): void {
    this.el.classList.add('penalty-wheel--show')
    this.syncUi()
  }

  hide(): void {
    this.stopLoop()
    this.clearAutoStop()
    this.isSpinning = false
    this.el.classList.remove('penalty-wheel--show')
    this.syncUi()
  }

  private create(): HTMLElement {
    const div = document.createElement('div')
    div.className = 'penalty-wheel'
    div.innerHTML = `
      <div class="penalty-wheel__card glass-panel">
        <button class="penalty-wheel__close" type="button" data-action="close" aria-label="关闭转盘">×</button>
        <div class="penalty-wheel__title">🎯 惩罚转盘</div>
        <div class="penalty-wheel__result">${this.selectedText}</div>
        <div class="penalty-wheel__board">
          <div class="penalty-wheel__pointer"></div>
          <div class="penalty-wheel__disc-wrap">
            <div class="penalty-wheel__disc"></div>
            <div class="penalty-wheel__labels"></div>
          </div>
        </div>
        <div class="penalty-wheel__actions">
          <button class="btn btn--danger" type="button" data-action="toggle-spin">开始</button>
          <button class="btn btn--ghost" type="button" data-action="toggle-settings">设置</button>
        </div>
        <div class="penalty-wheel__hint">开始后若不手动停止，将在 5 秒后自动停止。</div>
        <div class="penalty-wheel__settings" hidden>
          <label class="penalty-wheel__settings-label" for="penalty-wheel-items">转盘内容</label>
          <textarea id="penalty-wheel-items" class="input penalty-wheel__textarea" rows="3">${DEFAULT_ITEMS.join(',')}</textarea>
          <div class="penalty-wheel__settings-tip">使用英文逗号分隔，例如：1,2,4,6,8,小王,大王,平安夜</div>
          <div class="penalty-wheel__settings-actions">
            <button class="btn btn--primary btn--sm" type="button" data-action="save-settings">保存</button>
          </div>
        </div>
      </div>`
    return div
  }

  private bindEvents(): void {
    this.el.addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-action]')
      if (!btn) return

      const action = btn.dataset.action
      if (action === 'close') {
        this.hide()
        return
      }
      if (action === 'toggle-spin') {
        if (this.isSpinning) this.stopSpin()
        else this.startSpin()
        return
      }
      if (action === 'toggle-settings') {
        this.toggleSettings()
        return
      }
      if (action === 'save-settings') {
        this.saveSettings()
      }
    })
  }

  private startSpin(): void {
    if (this.items.length === 0 || this.isSpinning) return

    this.stopLoop()
    this.clearAutoStop()
    this.isSpinning = true

    const disc = this.el.querySelector<HTMLElement>('.penalty-wheel__disc-wrap')
    if (disc) disc.style.transition = 'none'

    this.spinInterval = window.setInterval(() => {
      this.currentRotation = (this.currentRotation + 12) % 360
      this.applyRotation()
    }, 16)

    this.autoStopTimer = window.setTimeout(() => this.stopSpin(), AUTO_STOP_DELAY_MS)
    this.syncUi()
  }

  private stopSpin(): void {
    if (!this.isSpinning) return

    this.stopLoop()
    this.clearAutoStop()
    this.isSpinning = false

    const extraRotation = 1080 + Math.random() * 720
    this.currentRotation += extraRotation

    const disc = this.el.querySelector<HTMLElement>('.penalty-wheel__disc-wrap')
    if (disc) disc.style.transition = 'transform 3.6s cubic-bezier(0.18, 0.88, 0.24, 1)'

    this.applyRotation()
    window.setTimeout(() => {
      this.selectedText = this.items[this.getSelectedIndex()] ?? this.selectedText
      this.syncUi()
    }, 3600)

    this.syncUi()
  }

  private stopLoop(): void {
    if (this.spinInterval !== null) {
      window.clearInterval(this.spinInterval)
      this.spinInterval = null
    }
  }

  private clearAutoStop(): void {
    if (this.autoStopTimer !== null) {
      window.clearTimeout(this.autoStopTimer)
      this.autoStopTimer = null
    }
  }

  private toggleSettings(): void {
    const settings = this.el.querySelector<HTMLElement>('.penalty-wheel__settings')
    if (!settings) return
    settings.hidden = !settings.hidden
  }

  private saveSettings(): void {
    const input = this.el.querySelector<HTMLTextAreaElement>('#penalty-wheel-items')
    if (!input) return

    const parsed = input.value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)

    this.items = parsed.length > 0 ? parsed : [...DEFAULT_ITEMS]
    if (parsed.length === 0) {
      input.value = DEFAULT_ITEMS.join(',')
    }

    this.renderWheel()
    this.selectedText = '准备开转'
    this.syncUi()

    const settings = this.el.querySelector<HTMLElement>('.penalty-wheel__settings')
    if (settings) settings.hidden = true
  }

  private renderWheel(): void {
    const disc = this.el.querySelector<HTMLElement>('.penalty-wheel__disc')
    const labels = this.el.querySelector<HTMLElement>('.penalty-wheel__labels')
    if (!disc || !labels) return

    const step = 360 / this.items.length
    const colors = this.items.map((_, index) => {
      const hue = Math.round((360 / this.items.length) * index)
      return `hsl(${hue} 85% 72%) ${step * index}deg ${step * (index + 1)}deg`
    })

    disc.style.background = `conic-gradient(${colors.join(', ')})`
    labels.innerHTML = this.items.map((item, index) => {
      const angle = step * index + step / 2
      return `<span class="penalty-wheel__label" style="transform: rotate(${angle}deg) translateY(-126px) rotate(${-angle}deg)">${item}</span>`
    }).join('')

    this.applyRotation()
  }

  private applyRotation(): void {
    const disc = this.el.querySelector<HTMLElement>('.penalty-wheel__disc-wrap')
    if (disc) disc.style.transform = `rotate(${this.currentRotation}deg)`
  }

  private getSelectedIndex(): number {
    const step = 360 / this.items.length
    const normalized = ((360 - (this.currentRotation % 360)) + 360) % 360
    return Math.floor(normalized / step) % this.items.length
  }

  private syncUi(): void {
    const result = this.el.querySelector<HTMLElement>('.penalty-wheel__result')
    const spinBtn = this.el.querySelector<HTMLElement>('[data-action="toggle-spin"]')

    if (result) result.textContent = this.selectedText
    if (spinBtn) spinBtn.textContent = this.isSpinning ? '停止' : '开始'
  }
}
